import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { AxiosResponse } from 'axios';

import WithSideBarAndNavBar from '../../layouts/withSideBarAndNavBar/withSideBarAndNavBar';
import './viewInformation.scss';
import MembersTabularView from './tabularView/membersTabularView';
import { IEmpInfoData, ISchData, ISchDataList, ISchList } from 'types/listOfEmployees';
import useLoadingStore from 'zustands/useLoadingStore';
import useApi from 'hooks/useApi';
import useErrorLog from 'hooks/useErrorLog';
import { IPayByPayStart } from 'types/viewInfomation';
import useAlertConfirmStore from 'zustands/useAlertConfirmStore';

const viewInformation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const empData: IEmpInfoData = location.state?.empData;

  const { setLoadingState } = useLoadingStore();
  const { setModalState } = useAlertConfirmStore();

  const { webApiPhp, webApiNode } = useApi();
  const { checkErrorLog } = useErrorLog();

  const [dateForPay, setDateForPay] = React.useState<Date>(new Date());
  const [infoData, setInfoData] = React.useState<IEmpInfoData | null>(null);
  const [payData, setPayData] = React.useState<IPayByPayStart>({
    PAY_TYPE: '0',
    PAY: '0',
    MEALS: '0',
    SELF_DRIVING: '0',
    BONUS: '0',
    INCENTIVE: '0',
  });
  const [monthPayData, setMonthPayData] = React.useState<{ [key: string]: string } | null>(null);
  const [schData, setSchData] = React.useState<ISchDataList[]>([]);

  const fetchEmpInfoData = async () => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiPhp('get', `/Employee/get?EMP_SEQ=${empData.EMP_SEQ}`);

      if (data.message === 'SUCCESS') {
        setInfoData(data.result);
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, ' /Employee/get');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 직원 일정정보 데이터 패칭
  const fetchScheduleData = async () => {
    try {
      const { data }: AxiosResponse['data'] = await webApiPhp(
        'get',
        `/Employee/get_schedules?EMP_SEQ=${empData.EMP_SEQ}&YEAR=${new Date().getFullYear()}&MONTH=${
          new Date().getMonth() + 1
        }`,
      );

      if (data.message === 'SUCCESS') {
        // 일자별 배열 생성
        let periodArr: { START: string; END: string }[] = data.result.reduce(
          (ac: { START: string; END: string }[] | 0, cv: ISchData, _i: number) => {
            const startDate = cv.START;
            const endDate = cv.END;
            if (ac === 0) {
              return [{ START: startDate, END: endDate }];
            } else {
              for (let i = 0; i < ac.length; i++) {
                if (ac[i].START === cv.START && ac[i].END === cv.END) break;
                if (ac[i].START !== cv.START || ac[i].END !== cv.END) {
                  return [{ START: startDate, END: endDate }, ...ac];
                }
              }
              return [...ac];
            }
          },
          0,
        );
        periodArr = periodArr.sort((a, b) => moment(b.START).toDate().getTime() - moment(a.START).toDate().getTime());
        // 일자별 데이터 삽입
        const schList = periodArr.map((i) => {
          const arr: ISchData[] = [];
          data.result.map((j: ISchData) => {
            if (i.START === j.START && i.END === j.END) {
              let item = { ...j };
              delete item.START;
              delete item.END;
              arr.push(item);
            }
          });
          return {
            START: i.START,
            END: i.END,
            data: arr,
          };
        });
        setSchData(schList);
      } else if (data.message === 'LIST_EMPTY') {
        setSchData([]);
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, ' /Employee/get_schedules');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  const fetchEmpPayData = async () => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiPhp('post', `/Employee/getEmpPay`, {
        EMP_SEQ: empData.EMP_SEQ,
        YEAR: moment(dateForPay).format('YYYY'),
        MONTH: moment(dateForPay).format('MM'),
      });
      if (data.message) {
        setPayData(data.message.PAY[0]);
        setMonthPayData({ ...data.message.INSURANCE[0], ...data.message.PAY_INFO[0] });
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, ' /Employee/getEmpPay');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 근로계약 날짜 변경
  const handleSelectDate = (date: Date) => {
    setDateForPay(date);
  };

  // 일정, 자율출퇴근 전환
  const toggleWorkType = async () => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/toggleCalendar`, {
        CALENDAR: infoData?.CALENDAR === '0' ? '1' : '0',
        EMP_SEQ_LIST: [{ EMP_SEQ: empData.EMP_SEQ }],
      });

      if (data.resultCode === 0) {
        fetchScheduleData();
        fetchEmpInfoData();
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, ' /auth/toggleCalendar');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 일정 삭제
  const removeSch = async (deleteSchList: string[]) => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/v1/schedule`, {
        DEL: deleteSchList,
      });

      if (data.resultCode === 0) {
        setModalState({
          isModalShow: true,
          title: '일정 삭제',
          desc: '완료되었습니다.',
          closeCallback: fetchScheduleData,
        });
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, ' /v1/schedule');
      setLoadingState({ isFetchLoading: false });
    } finally {
    }
  };

  useEffect(() => {
    if (!empData.EMP_SEQ) {
      navigate('/');
      return;
    }

    const loadData = async () => {
      await fetchScheduleData();
      await fetchEmpInfoData();
    };

    loadData();
  }, []);

  useEffect(() => {
    const loadPayData = async () => {
      await fetchEmpPayData();
    };

    loadPayData();
  }, [dateForPay]);

  if (!empData.EMP_SEQ) return null;

  const isWorking = infoData?.END === null || (infoData?.END !== null && moment(infoData?.END).isAfter(moment()));

  return (
    <WithSideBarAndNavBar>
      <div className="viewEmployeeInformation">
        <div className="breadCrumb">
          <div className="mainScreen">직원목록</div> {'>'}
          <div className="secondScreen">정보보기</div>
        </div>

        <div className="contentCard">
          <div className="leftContainer">
            <div className="profileImage">{infoData?.EMP_NAME.substring(1).substr(0, 2)}</div>
            <div className="name">{infoData?.EMP_NAME}</div>
            <div className="position">
              직책
              <div className="value">{infoData?.IS_MANAGER === '1' ? '매니저' : '스태프'}</div>
            </div>

            <div className="selectionWithActivity">
              <div className="stateCellContainer">
                <div
                  className="stateIndicator"
                  style={{
                    backgroundColor: isWorking ? '#E47C77' : ' #49AE9E',
                  }}
                />

                {isWorking ? '재직' : '퇴직'}
                {infoData?.probation === '1' ? (
                  moment(infoData?.probationDATE).isBefore(moment()) ? (
                    <div className="probation">수습완료</div>
                  ) : (
                    <div className="probation">수습중</div>
                  )
                ) : null}
              </div>
            </div>
          </div>
          <div className="rightContainer">
            <MembersTabularView
              empData={empData}
              infoData={infoData}
              schData={schData}
              date={dateForPay}
              handleSelectDate={handleSelectDate}
              payData={payData}
              monthPayData={monthPayData}
              toggleWorkType={toggleWorkType}
              removeSch={removeSch}
            />
          </div>
        </div>
      </div>
    </WithSideBarAndNavBar>
  );
};

export default viewInformation;
